/**
 * Shared color-by legend for the maneuver pages, rendered once per page below the selection
 * timeline strip so MAP / SCATTER / TIME SERIES / TABLE all read the same legend. DOM/CSS sibling
 * of ScatterHistoryColorLegend (the scatter/probability page legends) rather than the d3-drawn
 * legends/Maneuver.tsx it replaces. Lives inside #media-container, so it inherits the page's scale
 * transform — no --scale-factor math here.
 */
import { For, Show } from 'solid-js';
import type { ManeuverColorLegendItem } from '../../utils/maneuverColorLegend';
import CrewLegendTooltip, { createCrewLegendHover } from './CrewLegendTooltip';

/**
 * Titles for the color-by modes the maneuver pages offer. scatterHistoryColorByDisplayName is NOT
 * reusable here: it covers GRADE/TACK/UW-DW/SOURCE/DAY and returns 'Default' for the rest, which is
 * seven of the nine modes below.
 */
const MANEUVER_COLOR_BY_LABEL: Record<string, string> = {
  TWS: 'TWS',
  VMG: 'VMG %',
  TACK: 'Tack',
  SOURCE: 'Source',
  RACE: 'Race',
  STATE: 'State',
  CONFIG: 'Config',
  CREW: 'Crew',
  TESTS: 'Tests',
  YEAR: 'Year',
  EVENT: 'Event',
};

export interface ManeuverColorLegendProps {
  colorMode: string;
  items: ManeuverColorLegendItem[];
  /** Receives item.label (the raw group value), matching the page's handleLegendClick contract. */
  onItemClick?: (label: string) => void;
  /** Opens the baseline picker. Without it the baseline pill renders as static text. */
  onBaselineClick?: () => void;
  /** Override the "Color by <mode>" title. */
  title?: string;
}

export default function ManeuverColorLegend(props: ManeuverColorLegendProps) {
  const mode = () => String(props.colorMode || 'TWS').trim().toUpperCase();
  const modeLabel = () => MANEUVER_COLOR_BY_LABEL[mode()] ?? mode();
  const title = () => props.title ?? `Color by ${modeLabel()}`;
  const crewHover = createCrewLegendHover(() => mode() === 'CREW');
  const crewTip = (label: string) => mode() === 'CREW' && label.trim().toUpperCase() !== 'NONE';

  return (
    <Show when={props.items.length > 0}>
      {/* role="group", not "list": the entries are toggle buttons carrying aria-pressed, which the
          listitem role does not support. */}
      <div class="maneuver-color-legend" role="group" aria-label={`${title()} legend`}>
        <span class="maneuver-color-legend__title">{title()}</span>
        <For each={props.items}>
          {(item) => {
            const swatchClass = () => {
              let cls = 'maneuver-color-legend__swatch';
              if (item.markerShape === 'square') {cls += ' maneuver-color-legend__swatch--square';}
              if (item.isBaseline) {cls += ' maneuver-color-legend__swatch--baseline';}
              if (item.displayLabel.toUpperCase() === 'ALL') {cls += ' maneuver-color-legend__swatch--all';}
              return cls;
            };
            const swatch = (
              <span
                class={swatchClass()}
                style={{ 'background-color': item.color }}
                aria-hidden="true"
              />
            );
            const clickable = () => (item.isBaseline ? Boolean(props.onBaselineClick) : true);
            const onCrewEnter = (event: MouseEvent) => {
              if (crewTip(item.displayLabel)) {crewHover.show(event, item.displayLabel);}
            };
            const onCrewMove = (event: MouseEvent) => {
              if (crewTip(item.displayLabel)) {crewHover.move(event);}
            };
            return (
              <Show
                when={clickable()}
                fallback={
                  <span
                    class="maneuver-color-legend__item maneuver-color-legend__item--static"
                    onMouseEnter={onCrewEnter}
                    onMouseMove={onCrewMove}
                    onMouseLeave={crewHover.hide}
                  >
                    {swatch}
                    <span class="maneuver-color-legend__label">{item.displayLabel}</span>
                  </span>
                }
              >
                <button
                  type="button"
                  class={`maneuver-color-legend__item${item.isHighlight ? ' maneuver-color-legend__item--active' : ''}`}
                  aria-pressed={Boolean(item.isHighlight)}
                  onMouseEnter={onCrewEnter}
                  onMouseMove={onCrewMove}
                  onMouseLeave={crewHover.hide}
                  onClick={() => {
                    if (item.isBaseline) {
                      props.onBaselineClick?.();
                      return;
                    }
                    props.onItemClick?.(item.label);
                  }}
                >
                  {swatch}
                  <span class="maneuver-color-legend__label">{item.displayLabel}</span>
                </button>
              </Show>
            );
          }}
        </For>
      </div>
      <CrewLegendTooltip tip={crewHover.tip} />
    </Show>
  );
}
